"use client";
import React, { type FormEvent, useCallback, useState } from "react";
import * as z from "zod";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  isSubscribeToNewsletter: z.boolean().optional(),
  message: z.string().min(1, "Please enter a brief message."),
  name: z.string().min(1, "Please enter your name."),
  organization: z.string().optional(),
  telephoneNumber: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function ContactForm(): React.JSX.Element {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    isSubscribeToNewsletter: false,
    message: "",
    name: "",
    organization: "",
    telephoneNumber: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = event.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? (event.target as HTMLInputElement).checked : value,
      }));
    },
    [],
  );

  const validateForm = useCallback(() => {
    try {
      formSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof FormData, string>> = {};
        error.issues.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as keyof FormData] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  }, [formData]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!validateForm()) return;

      setIsSubmitting(true);
      try {
        const response = await fetch("/api/contact", {
          body: JSON.stringify(formData),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        });

        if (!response.ok) {
          throw new Error("Failed to submit form");
        }

        console.log("Form submitted successfully");
        setFormData({
          email: "",
          isSubscribeToNewsletter: false,
          message: "",
          name: "",
        });
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm],
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    void handleSubmit(event);
  };

  return (
    <form className="contact-form" onSubmit={onSubmit}>
      <div className="contact-form-cell">
        <label htmlFor="name">
          <em>Name*</em>
        </label>
        <input
          aria-invalid={!!errors.name}
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleInputChange}
        />
        {errors.name && <p role="alert">{errors.name}</p>}
      </div>

      <div className="contact-form-cell">
        <label htmlFor="email">
          <em>Email*</em>
        </label>
        <input
          aria-invalid={!!errors.email}
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
        />
        {errors.email && <p role="alert">{errors.email}</p>}
      </div>

      <div className="contact-form-cell contact-form-cell-message">
        <label htmlFor="message">
          <em>Message*</em>
        </label>
        <textarea
          aria-invalid={!!errors.message}
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
        />
        {errors.message && <p role="alert">{errors.message}</p>}
      </div>

      <div className="contact-form-cell contact-form-cell-subscribe">
        <label className="label-for-checkbox">
          <input
            checked={formData.isSubscribeToNewsletter}
            name="isSubscribeToNewsletter"
            type="checkbox"
            onChange={handleInputChange}
          />
          Subscribe to Newsletter
        </label>
      </div>

      <div className="contact-form-cell">
        <button
          data-is-submitting={isSubmitting ? "true" : "false"}
          disabled={isSubmitting}
          type="submit"
        >
          Submit
        </button>
      </div>
    </form>
  );
}
